<template>
  <div
    class="number-input"
    :class="{
      [`number-input--sizing-${renderFieldSizing}`]: true,
      'number-input--sizing-polyfill': enablePolyfill,
    }">
    <span
      v-if="!hideButtons"
      role="button"
      class="number-input__button number-input__button--decrease"
      :class="{ 'number-input__button--disabled': atMin || disabled }"
      :aria-label="`Decrease ${ariaContext}`"
      :aria-disabled="atMin || disabled"
      @mousedown="onMouseDown(-1)"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512">
        <path
          fill="currentColor"
          d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
      </svg>
    </span>
    <input
      :id="id"
      ref="fieldRef"
      v-model="internalValue"
      :style="{ width: enablePolyfill ? polyfillWidth + 'px' : undefined }"
      class="input-field number-input__field"
      type="number"
      :min="min ?? undefined"
      :max="max ?? undefined"
      :step="step"
      :disabled="disabled"
      :placeholder="placeholder"
      :aria-label="ariaContext !== 'value' ? `Number input for ${ariaContext}` : 'Number input'"
      @blur="clamp">
    <span
      v-if="!hideButtons"
      role="button"
      class="number-input__button number-input__button--increase"
      :class="{ 'number-input__button--disabled': atMax || disabled }"
      :aria-label="`Increase ${ariaContext}`"
      :aria-disabled="atMax || disabled"
      @mousedown="onMouseDown(1)"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512">
        <path
          fill="currentColor"
          d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
      </svg>
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useEventListener } from "@vueuse/core";

const emits = defineEmits<{
  "update:modelValue": [value: number | undefined];
}>();

const props = withDefaults(
  defineProps<{
    /**
     * The ID of the input field.
     */
    id?: string;
    /**
     * The value of the input field.
     */
    modelValue?: number | undefined;
    /**
     * The minimum value allowed.
     */
    min?: number | null;
    /**
     * The maximum value allowed.
     */
    max?: number | null;
    /**
     * The step value for incrementing or decrementing.
     */
    step?: number;
    /**
     * The placeholder text for the input field.
     */
    placeholder?: string;
    /**
     * Whether the input field is disabled.
     */
    disabled?: boolean;
    /**
     * Whether to show the increment and decrement buttons.
     */
    hideButtons?: boolean;
    /**
     * Whether the input should match the default input width,
     * or shrink to match its contents.
     */
    fieldSizing?: "fixed" | "content";
    /**
     * The context to create aria-label attributes.
     */
    ariaContext?: string;
  }>(),
  {
    id: undefined,
    modelValue: undefined,
    min: undefined,
    max: undefined,
    step: 1,
    placeholder: undefined,
    disabled: false,
    hideButtons: false,
    fieldSizing: "content",
    ariaContext: "value",
  },
);

const internalValue = ref<number | undefined>( props.modelValue );

watch( () => props.modelValue, ( val ) => {
  internalValue.value = val;
  clamp();
} );

watch( internalValue, ( val: number | undefined ) => {
  emits( "update:modelValue", val );
} );

const atMin = computed( () => {
  return props.min !== undefined && props.min !== null && internalValue.value === props.min;
} );

const atMax = computed( () => {
  return props.max !== undefined && props.max !== null && internalValue.value === props.max;
} );

let initialTimer: ReturnType<typeof setTimeout> | undefined;
let repeatInterval: ReturnType<typeof setInterval> | undefined;

const initialChangeDelayMs = 400;
const repeatChangeMs = 80;

function onMouseDown ( dir: number ) {
  if ( props.disabled ) {
    return;
  }

  if ( dir < 0 ) {
    onDecrease();
  } else if ( dir > 0 ) {
    onIncrease();
  }

  initialTimer = setTimeout( () => {
    clearTimeout( initialTimer );
    initialTimer = undefined;
    repeatInterval = setInterval( () => {
      if ( dir < 0 ) {
        onDecrease();
      } else if ( dir > 0 ) {
        onIncrease();
      }

      if ( atMin.value || atMax.value ) {
        clearInterval( repeatInterval );
        repeatInterval = undefined;
      }
    }, repeatChangeMs );
  }, initialChangeDelayMs );
}

function onMouseUp () {
  if ( initialTimer ) {
    clearTimeout( initialTimer );
    initialTimer = undefined;
  }

  if ( repeatInterval ) {
    clearInterval( repeatInterval );
    repeatInterval = undefined;
  }
}

function onDecrease () {
  if ( !internalValue.value ) {
    internalValue.value = 0;
  }

  if ( !props.disabled && !atMin.value ) {
    internalValue.value -= props.step;
    clamp();
  }
}

function onIncrease () {
  if ( !internalValue.value ) {
    internalValue.value = 0;
  }

  if ( !props.disabled && !atMax.value ) {
    internalValue.value += props.step;
    clamp();
  }
}

function clamp () {
  if ( internalValue.value === undefined ) {
    return;
  }

  if ( props.min !== undefined && props.min !== null ) {
    internalValue.value = Math.max( props.min ?? 0, internalValue.value || 0 );
  }

  if ( props.max !== undefined && props.max !== null ) {
    internalValue.value = Math.min( props.max ?? 0, internalValue.value || 0 );
  }
}

const renderFieldSizing = computed( () => props.fieldSizing || "content" );

const polyfillWidth = ref( 0 );
const fieldRef = ref<HTMLInputElement | null>( null );

const enablePolyfill = computed( () => {
  if ( !fieldRef.value ) {
    return false;
  }

  if ( props.fieldSizing !== "content" ) {
    return false;
  }

  // If the browser supports the native property, do nothing
  const supportsFieldSizingContent
    = typeof CSS !== "undefined"
    && typeof CSS.supports === "function"
    && CSS.supports( "field-sizing", "content" );
  if ( supportsFieldSizingContent ) {
    return false;
  }

  return true;
} );

function updateFieldSize () {
  if ( enablePolyfill.value && fieldRef.value ) {
    const input = fieldRef.value;
    const computedStyle = window.getComputedStyle( input );

    // Create a hidden span to measure text width
    const span = document.createElement( "span" );

    // Copy relevant font and text styles
    span.style.fontSize = computedStyle.fontSize;
    span.style.fontFamily = computedStyle.fontFamily;
    span.style.fontWeight = computedStyle.fontWeight;
    span.style.fontStyle = computedStyle.fontStyle;
    span.style.textTransform = computedStyle.textTransform;
    span.style.letterSpacing = computedStyle.letterSpacing;
    span.style.whiteSpace = "pre";
    span.style.boxSizing = computedStyle.boxSizing;

    // (Optional) If you want to include horizontal padding, copy them:
    const paddingLeft = parseFloat( computedStyle.paddingLeft ) || 0;
    const paddingRight = parseFloat( computedStyle.paddingRight ) || 0;
    const extraPadding = 2; // small extra so text doesn’t get clipped

    // Hide the span off-screen so it doesn't affect layout
    span.style.position = "absolute";
    span.style.visibility = "hidden";
    document.body.appendChild( span );

    // Use the input's current value or its placeholder as fallback
    span.textContent = input.value || input.placeholder || "";

    // Measure the text width
    const textWidth = Math.ceil( span.getBoundingClientRect().width );
    polyfillWidth.value = textWidth + paddingLeft + paddingRight + extraPadding;

    document.body.removeChild( span );
  }
}

watch( internalValue, () => {
  nextTick( () => {
    updateFieldSize();
  } );
} );

useEventListener( fieldRef, "input", updateFieldSize );

onMounted( () => {
  nextTick( () => {
    updateFieldSize();
  } );
} );

// Ensure the input number is set to minimum value on component load
clamp();
</script>

<style lang="scss" scoped>
.number-input {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid var(--input-border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-background-color);
  transition: box-shadow 0.2s ease-out, color 0.2s ease-out,
  border-color 0.2s ease-out, background-color 0.2s ease-out;

  &:focus-within {
    box-shadow: 0 0 0 0.2rem var(--color-primary-400);
    border-color: var(--color-primary-400);
  }

  &__button {
    border: none;
    aspect-ratio: 1;
    min-width: 36px;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    user-select: none;
    transition: background-color var(--transition-time) ease-out;

    svg {
      width: 0.8rem;
      color: var(--text-color);
    }

    &:hover {
      cursor: pointer;
      background-color: var(--color-primary-100);
    }

    &--disabled,
    &.disabled,
    &[disabled],
    &:disabled {
      background-color: var(--color-gray-100);

      svg {
        color: var(--color-gray-400);
      }

      &:hover {
        cursor: not-allowed;
        background-color: var(--color-gray-100);
      }
    }

    &--decrease {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 1px solid var(--input-border-color);
    }

    &--increase {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left: 1px solid var(--input-border-color);
    }
  }

  .input-field {
    border-radius: 0;
    border: none;
    -moz-appearance: textfield;
    min-width: 4ch;
    font-variant-numeric: tabular-nums;
    flex-shrink: 1;
    max-width: 100%;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      box-shadow: none;
    }
  }

  &--sizing-fixed {
    width: 100%;

    .input-field {
      width: 100%;
      field-sizing: fixed;
    }
  }

  &--sizing-content {
    width: auto;

    .input-field {
      max-width: 100%;
      width: auto;
      field-sizing: content;
    }
  }
}
</style>
